/**
 * Public types of the H5P translation semantics walker. Keep this module free
 * of Angular/jQuery/app imports — editor, player and counters all consume it.
 */

/** The four kinds of translatable units the walker emits. */
export const TRANSLATABLE_FIELD_TYPES = {
  TEXT: 'text',
  HTML: 'html',
  IMAGE: 'image',
  MEDIA: 'media',
} as const;

export type TranslatableFieldType =
  (typeof TRANSLATABLE_FIELD_TYPES)[keyof typeof TRANSLATABLE_FIELD_TYPES];

/** The only `translatableCondition` value in use (conditional fields). */
export const TRANSLATABLE_CONDITION_SOURCE_NON_EMPTY = 'sourceNonEmpty';

/**
 * `translatableAs: 'url'`: translatable only when the source is an external
 * video URL; a local upload stays universal. No semantics field uses it today
 * (video sources are universal) — the generic gate is kept for future fields.
 */
export const TRANSLATABLE_AS_URL = 'url';

/**
 * One field definition of a `semantics.json`. Foreign JSON — only what the
 * walker reads is declared; the rest rides the index signature.
 */
export interface H5PSemanticsField {
  readonly name?: string;
  readonly type?: string;
  readonly label?: string;
  readonly entity?: string;
  readonly widget?: string;
  readonly common?: boolean;
  readonly translatable?: boolean;
  readonly translatableCondition?: string;
  /** Inert marker; `'url'` = translatable only when the source is a URL. */
  readonly translatableAs?: string;
  readonly isSubContent?: boolean;
  /** List-of-text -> list-of-group migration marker (SortParagraphs). */
  readonly stringItemField?: string;
  /** Sub-fields of a `group`. */
  readonly fields?: readonly H5PSemanticsField[];
  /** Item definition of a `list`. */
  readonly field?: H5PSemanticsField;
  /** Allowed libraries of a `library` field (uber names). */
  readonly options?: readonly unknown[];
  readonly [key: string]: unknown;
}

/** A whole semantics.json (the top-level array of field definitions). */
export type H5PSemantics = readonly H5PSemanticsField[];

/** Content params — foreign JSON, structure mirrors semantics storage. */
export type H5PParams = Record<string, unknown>;

/**
 * An H5P file object as stored in params for image/file/video/audio fields
 * (e.g. `{ path, mime, width, height, copyright }`).
 */
export interface H5PFileObject {
  readonly path?: string;
  readonly mime?: string;
  readonly [key: string]: unknown;
}

/**
 * A value in the flat translation map: the string for text/html units, the
 * whole file object/array for media, the `params` object for library embeds.
 */
export type TranslationValue =
  | string
  | Readonly<Record<string, unknown>>
  | ReadonlyArray<Readonly<Record<string, unknown>>>;

/**
 * Flat translation map for one target language:
 * `"<itemKey>:<fieldName>" -> value`.
 */
export type TranslationMap = Readonly<Record<string, TranslationValue>>;

/**
 * Resolves an embedded library's semantics by uber name. `undefined` makes the
 * walker skip that subtree gracefully.
 */
export type SemanticsResolverFn = (
  uberName: string,
) => H5PSemantics | undefined;

/**
 * Sub-library semantics source: a resolver function, or a record keyed by uber
 * name whose lookups fall back to the bare machine name.
 */
export type SemanticsResolver =
  | SemanticsResolverFn
  | Readonly<Record<string, H5PSemantics>>;

export interface WalkerOptions {
  /** Semantics for embedded libraries. Omit to skip all embeds. */
  readonly resolveSemantics?: SemanticsResolver;
}

export interface MergeOptions extends WalkerOptions {
  /** Target language code. Reserved — the merge does not stamp it anywhere. */
  readonly language?: string;
}

/** Info about the item (question / list entry / page group) owning a field. */
export interface TranslatableItemInfo {
  /**
   * Stable key (`subContentId ?? id`), the group-name path for module-level
   * fields, or `null` for legacy items that cannot be keyed yet.
   */
  readonly itemKey: string | null;
  /** Human label of the owning item/group (for editor grouping). */
  readonly label: string;
  /** Uber name of the library instance the item belongs to (embeds only). */
  readonly library?: string;
  /** Index of the item within its list, when it is a list item. */
  readonly index?: number;
  /** Absolute params path of the owning item. */
  readonly path: string;
}

/** One translatable unit, in stable document order. */
export interface TranslatableField {
  /** Same as `item.itemKey` (convenience). */
  readonly itemKey: string | null;
  /**
   * Field name relative to the owning item — a single name, or a slash path
   * for fields nested in plain groups.
   */
  readonly fieldName: string;
  /** `"<itemKey>:<fieldName>"`, or `null` when the item cannot be keyed. */
  readonly mapKey: string | null;
  /** Absolute human-readable params path, e.g. `'wizard/questions[0]/params/question'`. */
  readonly path: string;
  /**
   * Programmatic path of the unit's value inside params. For library-backed
   * image/media units it points at the embed's `params` key.
   */
  readonly pathSegments: ReadonlyArray<string | number>;
  /** Field label from semantics (falls back to the field name). */
  readonly label: string;
  readonly type: TranslatableFieldType;
  /** Semantics widget of the field (e.g. `'colorSelector'`), when declared. */
  readonly widget?: string;
  /** Current source-language value (may be `undefined` when unset). */
  readonly sourceValue: TranslationValue | undefined;
  /** True when `translatableCondition: 'sourceNonEmpty'` applies. */
  readonly conditional: boolean;
  /**
   * True for image/media units backed by an embedded asset library
   * (`H5P.Image`, …); `embedLibrary` then carries the embed's uber name.
   */
  readonly libraryAsset: boolean;
  readonly embedLibrary?: string;
  /**
   * Keys of all keyed ancestors, outermost first, ending with the owning item.
   * Lets progress attribute a field to every enclosing item.
   */
  readonly keyChain: readonly string[];
  readonly item: TranslatableItemInfo;
}

export interface ProgressCount {
  readonly translated: number;
  readonly total: number;
}

export interface TranslationProgress extends ProgressCount {
  /**
   * Per-item subtotals. Entries overlap hierarchically, so the module totals
   * are NOT their sum — those count every field exactly once.
   */
  readonly perItem: Readonly<Record<string, ProgressCount>>;
}

export interface MintResult {
  /** Deep copy of the input params with the missing ids assigned. */
  readonly params: H5PParams;
  /** Whether any id was minted (or legacy item restructured). */
  readonly changed: boolean;
}
